import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { COMPANY, getPhoneLink, getEmailLink } from "@/config/company";
import { SmoothLink as Link } from "@/components/navigation";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

const footerLinks = {
  treatments: [
    { label: "Dental Implants & Veneers", href: "/treatments/dental/" },
    { label: "Heart Surgery & Cardiac Care", href: "/treatments/cardiac/" },
    { label: "Knee & Hip Replacement", href: "/treatments/orthopedics/" },
    { label: "Cancer Treatment & Oncology", href: "/treatments/oncology/" },
    { label: "Browse All Medical Treatments", href: "/treatments/" },
  ],
  resources: [
    { label: "Medical Tourism Facilitators Guide", href: "/blog/role-medical-tourism-facilitators-india-guide/" },
    { label: "Medical Tourism India Guide", href: "/medical-tourism-india/" },
    { label: "Compare Surgery Costs India vs USA", href: "/compare-costs/" },
    { label: "JCI-Accredited Hospitals in India", href: "/hospitals/" },
    { label: "Expert Doctors & Specialists", href: "/doctors/" },
    { label: "Patient Stories & Articles", href: "/blog/" },
  ],
  support: [
    { label: "Get Free Treatment Quote", href: "/contact/" },
    { label: "All-on-4 Dental Implants Cost", href: "/treatments/dental/all-on-4-implants/" },
    { label: "Heart Bypass Surgery in India", href: "/treatments/cardiac/heart-bypass-cabg/" },
    { label: "Knee Replacement Cost India", href: "/treatments/orthopedics/knee-replacement/" },
  ],
};

export function Footer() {
  const { trackPhoneClick, trackCTAClick } = useAnalyticsContext();
  return (
    <footer className="bg-secondary text-secondary-foreground">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img src="/logos/logo-full-white.svg" alt={COMPANY.name} className="h-10 w-auto" width="188" height="40" />
            </Link>
            <p className="text-secondary-foreground/80 mb-6 max-w-sm">
              {COMPANY.tagline}. We combine AI efficiency with human empathy to transform medical tourism.
            </p>
            <div className="flex flex-col gap-3">
              <a 
                href={getEmailLink("care")} 
                className="flex items-center gap-3 text-secondary-foreground/80 hover:text-accent transition-colors"
                onClick={() => trackCTAClick(COMPANY.email.care, 'footer', 'email')}
              >
                <Mail className="w-5 h-5" />
                <span>{COMPANY.email.care}</span>
              </a>
              <a 
                href={getPhoneLink()} 
                className="flex items-center gap-3 text-secondary-foreground/80 hover:text-accent transition-colors"
                onClick={() => trackPhoneClick('footer')}
              >
                <Phone className="w-5 h-5" />
                <span>{COMPANY.phone.display}</span>
              </a>
              <div className="flex items-start gap-3 text-secondary-foreground/80">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{COMPANY.address.multiline}</span>
              </div>
            </div>
          </div>

          {/* Treatments */}
          <div>
            <h2 className="font-display font-semibold text-lg mb-4 text-secondary-foreground">Treatments</h2>
            <ul className="space-y-2">
              {footerLinks.treatments.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-secondary-foreground/70 hover:text-accent transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h2 className="font-display font-semibold text-lg mb-4 text-secondary-foreground">Resources</h2>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-secondary-foreground/70 hover:text-accent transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h2 className="font-display font-semibold text-lg mb-4 text-secondary-foreground">Support</h2>
            <ul className="space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-secondary-foreground/70 hover:text-accent transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-secondary-foreground/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-secondary-foreground/60 text-sm text-center md:text-left">
              © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="#" 
                className="text-secondary-foreground/60 hover:text-accent transition-colors" 
                aria-label="Facebook"
                onClick={() => trackCTAClick('Facebook', 'footer', 'social')}
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="text-secondary-foreground/60 hover:text-accent transition-colors" 
                aria-label="Instagram"
                onClick={() => trackCTAClick('Instagram', 'footer', 'social')}
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="text-secondary-foreground/60 hover:text-accent transition-colors" 
                aria-label="LinkedIn"
                onClick={() => trackCTAClick('LinkedIn', 'footer', 'social')}
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="text-secondary-foreground/60 hover:text-accent transition-colors" 
                aria-label="YouTube"
                onClick={() => trackCTAClick('YouTube', 'footer', 'social')}
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/privacy-policy/" className="text-secondary-foreground/60 hover:text-accent transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service/" className="text-secondary-foreground/60 hover:text-accent transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
